import React, { useState } from 'react';

export default function StepOne({ website: initialWebsite, onAnalyze, isLoading }) {
  const [website, setWebsite] = useState(initialWebsite || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (website && !isLoading) {
      onAnalyze(website);
    }
  };

  return (
    <div className="text-center">
      {/* Header */}
      <div className="mb-12">
        <div className="mb-6">
          <span 
            className="text-sm font-medium px-4 py-2 rounded-full inline-flex items-center"
            style={{ 
              backgroundColor: 'var(--gradient-subtle)',
              color: 'var(--color-primary)'
            }}
          >
            <span 
              className="w-2 h-2 rounded-full mr-2 animate-pulse"
              style={{ backgroundColor: 'var(--color-accent)' }}
            ></span>
            AI Business Analysis
          </span>
        </div>
        <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ color: 'var(--color-gray-900)' }}>
          Enter Your Business <span style={{ color: 'var(--color-primary)' }}>Website</span>
        </h1>
        <p className="text-xl leading-relaxed max-w-2xl mx-auto" style={{ color: 'var(--color-gray-600)' }}>
          Our AI will analyze your website to understand your business model, market position, and growth potential.
        </p>
      </div>

      {/* Website Input Form */}
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
        <div 
          className="rounded-2xl shadow-xl p-6 border"
          style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            borderColor: 'var(--color-gray-100)'
          }}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="url"
              placeholder="https://yourbusiness.com"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="form-input flex-1 text-lg"
              disabled={isLoading}
              required
            />
            <button
              type="submit"
              disabled={!website || isLoading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Analyzing...
                </div>
              ) : (
                'Analyze Business'
              )}
            </button>
          </div>
          <p className="text-xs mt-4 text-center" style={{ color: 'var(--color-gray-500)' }}>
            Enter your website above to get started
          </p>
        </div>
      </form>

      {/* Features Grid */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
        <div className="text-center p-6 rounded-2xl transition-all duration-300 hover:bg-white hover:shadow-lg">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--color-gray-900)' }}>Business Model Analysis</h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--color-gray-600)' }}>Revenue streams, customer base, and operational structure</p>
        </div>

        <div className="text-center p-6 rounded-2xl transition-all duration-300 hover:bg-white hover:shadow-lg">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300"
            style={{ backgroundColor: 'var(--color-accent)' }}
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--color-gray-900)' }}>Market Position</h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--color-gray-600)' }}>Competitive advantages and market differentiation</p>
        </div>

        <div className="text-center p-6 rounded-2xl transition-all duration-300 hover:bg-white hover:shadow-lg">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300"
            style={{ backgroundColor: 'var(--color-slate)' }}
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--color-gray-900)' }}>Growth Potential</h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--color-gray-600)' }}>Scalability factors and expansion opportunities</p>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="mt-16 text-center">
        <div className="flex flex-wrap justify-center items-center gap-8 text-sm" style={{ color: 'var(--color-gray-500)' }}>
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" style={{ color: 'var(--color-success)' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Secure analysis</span>
          </div>
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" style={{ color: 'var(--color-success)' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>No signup required</span>
          </div>
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" style={{ color: 'var(--color-success)' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Completely confidential</span>
          </div>
        </div>
      </div>
    </div>
  );
} 